import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { getAccessToken } from '../Auth/AuthStore'
import styles from './ActivitySidebar.module.css'
import communityicon from '../../assets/icons/community.svg'
import drawicon from '../../assets/icons/draw.svg'
import houseicon from '../../assets/icons/house.svg'
import humanicon from '../../assets/icons/human.svg'
import chaticon from '../../assets/icons/chat.svg'
import paletteicon from '../../assets/icons/palette.svg'
import medalicon from '../../assets/icons/medal.svg'

const ActivitySidebar = () => {
  const navigate = useNavigate()
  const [completedPrograms, setCompletedPrograms] = useState([])
  const [loading, setLoading] = useState(true)

  const handleQuickStartClick = (index) => {
    switch (index) {
      case 0: // HTP 테스트
        navigate('/drawing', {
          state: {
            preselectedTopicId: 'htp-house'
          }
        })
        break
      case 1: // 일기
        const today = new Date()
        navigate('/diary', {
          state: {
            showWriteDiaryModal: true,
            selectedDate: today
          }
        })
        break
      case 2: // 자유 그리기
        navigate('/drawing', {
          state: {
            freeDrawing: true
          }
        })
        break
      case 3: // 커뮤니티
        navigate('/community', {
          state: {
            showCreatePostModal: true
          }
        })
        break
      default:
        break
    }
  }

  const quickStartItems = [
    {
      title: 'HTP 테스트',
      subtitle: '집 그림 그리기',
      color: '#DBEAFE',
      textColor : '#0029CD',
      icon: <img src={houseicon} alt="house" />
    },
    {
      title: '일기', 
      subtitle: '오늘 날짜의 일기 작성하기',
      color: '#DAFFDA',
      textColor : '#00510F',
      icon: <img src={humanicon} alt="human" />
    },
    {
      title: '자유 그리기',
      subtitle: '마음대로 표현하기',
      color: '#EDE9FE',
      textColor : '#7100B3',
      icon: <img src={drawicon} alt="draw" />
    },
    {
      title: '커뮤니티',
      subtitle: '작품 공유하기',
      color: '#FFF1B8',
      textColor : '#976200',
      icon: <img src={communityicon} alt="community" />
    }
  ]

  // 날짜 포맷팅 함수
  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}.${month}.${day}`
  }

  // 완주한 프로그램 목록 가져오기
  useEffect(() => {
    const fetchCompletedPrograms = async () => {
      try {
        setLoading(true)
        const token = getAccessToken()
        if (!token) {
          setLoading(false)
          return
        }

        const response = await axios.get(
          `${import.meta.env.VITE_API_BASE}/api/programs/participated`,
    {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        const programs = response.data?.data?.therapyProgramInfos || []
        // 최신순으로 정렬 (programEndDate 기준)
        const sortedPrograms = programs.sort((a, b) => {
          const dateA = new Date(b.programEndDate)
          const dateB = new Date(a.programEndDate)
          return dateA - dateB
        })
        setCompletedPrograms(sortedPrograms)
      } catch (error) {
        console.error('완주한 프로그램 목록 조회 실패:', error)
        setCompletedPrograms([])
      } finally {
        setLoading(false)
      }
    }

    fetchCompletedPrograms()
  }, [])

  return (
    <div className={styles.sidebar}>
      {/* 빠른 시작 섹션 */}
      <section className={styles.quickStartSection}>
        <h3 className={styles.sectionTitle}>빠른 시작</h3>
        <div className={styles.quickStartGrid}>
          {quickStartItems.map((item, index) => (
            <div 
              key={index}
              className={styles.quickStartItem}
              style={{ backgroundColor: item.color, color: item.textColor }}
              onClick={() => handleQuickStartClick(index)}
            >
              <div className={styles.quickStartContent}>
                <h4>{item.title}</h4>
                <p>{item.subtitle}</p>
              </div>
              <div className={styles.quickStartIcon}>
                {item.icon}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 완주한 여정 섹션 */}
      <section className={styles.recentActivitiesSection}>
        <h3 className={styles.sectionTitle}>완주한 여정</h3>
        <div className={styles.activitiesList}>
          {loading ? (
            <div className={styles.loadingMessage}>로딩 중...</div>
          ) : completedPrograms.length === 0 ? (
            <div className={styles.emptyMessage}>완주한 여정이 없습니다</div>
          ) : (
            completedPrograms.map((program, index) => {
              const startDate = formatDate(program.programStartDate)
              const endDate = formatDate(program.programEndDate)
              const colors = ['#DBEAFE', '#DCFCE7', '#EDE9FE', '#FFF1B8', '#FFDCB9']
              const color = '#DCFCE7'
              
              return (
                <div 
                  key={program.therapyProgramId || index} 
                  className={styles.activityItem}
                  onClick={() => {
                    if (program.therapyProgramId) {
                      navigate(`/programs/${program.therapyProgramId}/result`)
                    }
                  }}
                >
              <div 
                className={styles.activityIcon}
                    style={{ backgroundColor: color }}
              >
                    <img src={medalicon} alt="medal" className={styles.icon} />
              </div>
              <div className={styles.activityContent}>
                    <h4>프로그램 #{program.therapyProgramId} 완주</h4>
                    <p>{startDate} ~ {endDate}</p>
              </div>
            </div>
              )
            })
          )}
        </div>
      </section>
    </div>
  )
}

export default ActivitySidebar